import React, { useEffect, useState } from "react";

import { ButtonStandard, RadioButtons, Spinner } from "@performyard/py-components";
import { Text } from "@performyard/py-components";

import ContentModal from "../../../../../components/Modal/Templates/ContentModal";
import TruncatedText from "../../../../../components/Text/TruncatedText";

import {
	useSaveSelectedCalendar,
	useUserCalendarIntegrationStatus,
	useUserCalendars,
} from "../../../../../api/hooks/defs/calendar-integration";

type CalendarSelectionModalProps = {
	isOpen: boolean;
	onClose: () => void;
};

type Calendar = {
	id: string;
	name: string;
	primary?: boolean;
};

export const CalendarSelectionModal = ({ isOpen, onClose }: CalendarSelectionModalProps) => {
	const [selectedCalendarId, setSelectedCalendarId] = useState<string>("");

	// Get user calendar status to determine provider
	const { data: userCalendarStatus } = useUserCalendarIntegrationStatus();
	const provider = userCalendarStatus?.provider;

	// Fetch calendars
	const { data: calendarsData, isLoading: isLoadingCalendars } = useUserCalendars({
		provider: provider as "google" | "outlook",
		enabled: !!provider,
	});

	const { mutateAsync: saveSelectedCalendar, isPending: isSaving } = useSaveSelectedCalendar();

	// Sort calendars: primary first, then alphabetical
	const sortedCalendars = React.useMemo(() => {
		const calendars = calendarsData?.calendars || [];
		return [...calendars].sort((a, b) => {
			// Primary calendar always comes first
			if (a.primary && !b.primary) return -1;
			if (!a.primary && b.primary) return 1;
			// Then sort alphabetically by name
			return a.name.localeCompare(b.name);
		});
	}, [calendarsData]);

	// Set the selectedCalendarId to the user's currently saved calendar, or primary as fallback
	useEffect(() => {
		if (sortedCalendars.length > 0 && !selectedCalendarId) {
			// First try to use the user's currently saved calendar
			const savedCalendarId = userCalendarStatus?.selectedCalendarId;
			if (savedCalendarId && sortedCalendars.some((cal) => cal.id === savedCalendarId)) {
				setSelectedCalendarId(savedCalendarId);
			} else {
				// Fallback to primary calendar if saved calendar is not found
				const primaryCalendar = sortedCalendars.find((cal) => cal.primary);
				if (primaryCalendar) {
					setSelectedCalendarId(primaryCalendar.id);
				}
			}
		}
	}, [sortedCalendars, selectedCalendarId, userCalendarStatus?.selectedCalendarId]);

	const handleSave = async () => {
		if (!selectedCalendarId) return;

		try {
			await saveSelectedCalendar({ calendarId: selectedCalendarId });
			onClose();
		} catch (error) {
			console.error("Error saving selected calendar:", error);
		}
	};

	const handleClose = () => {
		setSelectedCalendarId("");
		onClose();
	};

	// Transform calendars to RadioButtons options with alternating background colors
	const calendarOptions = sortedCalendars.map((calendar: Calendar) => ({
		label: (
			<div className="flex flex-1 items-center">
				<div className="flex items-center gap-1">
					<TruncatedText
						text={calendar.name}
						maxWidth={calendar.primary ? 250 : 300}
						size="paragraph-3"
						weight="regular"
						color="neutral-9"
						useTooltip="on-truncate"
					/>
					{calendar.primary && (
						<Text size="paragraph-3" weight="regular" color="neutral-6">
							(Primary)
						</Text>
					)}
				</div>
			</div>
		),
		value: calendar.id,
	}));

	return (
		<ContentModal
			isOpen={isOpen}
			handleClose={handleClose}
			size="large"
			title={
				<Text weight="semi-bold" size="sub-heading">
					Which calendar would you like to connect?
				</Text>
			}
			subTitle={
				<Text weight="regular" size="paragraph-2">
					You can only have one selected calendar
				</Text>
			}
			buttons={[
				<ButtonStandard
					key="cancel"
					color="red"
					onClick={handleClose}
					size="large"
					disabled={isLoadingCalendars || isSaving}
				>
					Cancel
				</ButtonStandard>,
				<ButtonStandard
					key="save"
					onClick={handleSave}
					size="large"
					disabled={!selectedCalendarId || isLoadingCalendars || isSaving}
				>
					Change Calendar
				</ButtonStandard>,
			]}
		>
			{isLoadingCalendars ? (
				<div className="flex items-center justify-center py-8">
					<Spinner size={24} />
				</div>
			) : (
				<div className="max-h-80 overflow-y-auto">
					<div className="space-y-0">
						{sortedCalendars.map((calendar: Calendar, index: number) => (
							<div
								key={calendar.id}
								className={`flex h-6 items-center px-2 py-1 ${
									index % 2 === 0 ? "bg-neutral-1" : "bg-neutral-0"
								}`}
							>
								<RadioButtons
									id={`calendar-selection-${index}`}
									options={[calendarOptions[index]]}
									value={selectedCalendarId}
									onChange={setSelectedCalendarId}
									orientation="vertical"
									size="small"
									dangerouslySetClassName="flex-1 [&>div]:flex [&>div]:items-center [&>div]:h-full [&>div]:gap-2"
								/>
							</div>
						))}
					</div>
				</div>
			)}
		</ContentModal>
	);
};
